import React, { useEffect, useState } from 'react';
import { PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { collection, onSnapshot } from 'firebase/firestore';
import { firestore } from '../../database/firebase';
import { UserGroupIcon } from '@heroicons/react/24/outline';

const ROLE_COLORS = {
  client: '#2563EB',
  alumni: '#EF4444',
  coach: '#10B981',
  businessAdvisor: '#F59E0B',
};
const ROLE_LABELS = ['client', 'alumni', 'coach', 'businessAdvisor'];

const UserProgressionChart = () => {
  const [userData, setUserData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [roles, setRoles] = useState(['All', ...ROLE_LABELS]);
  const [organizations, setOrganizations] = useState(['All']);
  const [selectedRole, setSelectedRole] = useState('All');
  const [selectedOrganization, setSelectedOrganization] = useState('All');
  const [dateRange, setDateRange] = useState({ from: null, to: null });

  useEffect(() => {
    const orgs = new Set();
    const unsubClients = onSnapshot(collection(firestore, 'clients'), snap => {
      snap.docs.forEach(doc => {
        if (doc.data().organization) orgs.add(doc.data().organization);
      });
      setOrganizations(['All', ...Array.from(orgs)]);
    });
    return () => unsubClients();
  }, []);

  useEffect(() => {
    setLoading(true);
    const unsubs = [];
    const counts = { client: 0, alumni: 0, coach: 0, businessAdvisor: 0 };
    const filterByDate = createdAt => {
      if (!dateRange.from || !dateRange.to) return true;
      if (!createdAt) return false;
      const d = createdAt.seconds ? new Date(createdAt.seconds * 1000) : new Date(createdAt);
      return d >= dateRange.from && d <= dateRange.to;
    };
    const filterByOrg = org => selectedOrganization === 'All' || org === selectedOrganization;
    const filterByRole = role => selectedRole === 'All' || role === selectedRole;
    unsubs.push(
      onSnapshot(collection(firestore, 'clients'), snap => {
        counts.client = snap.docs.filter(
          doc =>
            filterByDate(doc.data().createdAt) &&
            filterByOrg(doc.data().organization) &&
            filterByRole('client')
        ).length;
        setUserData(
          ROLE_LABELS.map(role => ({
            name: role.charAt(0).toUpperCase() + role.slice(1),
            value: counts[role],
            color: ROLE_COLORS[role],
          }))
        );
        setLoading(false);
      })
    );
    unsubs.push(
      onSnapshot(collection(firestore, 'alumni'), snap => {
        counts.alumni = snap.docs.filter(
          doc =>
            filterByDate(doc.data().createdAt) &&
            filterByOrg(doc.data().organization) &&
            filterByRole('alumni')
        ).length;
        setUserData(
          ROLE_LABELS.map(role => ({
            name: role.charAt(0).toUpperCase() + role.slice(1),
            value: counts[role],
            color: ROLE_COLORS[role],
          }))
        );
        setLoading(false);
      })
    );
    unsubs.push(
      onSnapshot(collection(firestore, 'coaches'), snap => {
        counts.coach = snap.docs.filter(
          doc =>
            filterByDate(doc.data().createdAt) &&
            filterByOrg(doc.data().organization) &&
            filterByRole('coach')
        ).length;
        setUserData(
          ROLE_LABELS.map(role => ({
            name: role.charAt(0).toUpperCase() + role.slice(1),
            value: counts[role],
            color: ROLE_COLORS[role],
          }))
        );
        setLoading(false);
      })
    );
    unsubs.push(
      onSnapshot(collection(firestore, 'moderator'), snap => {
        counts.businessAdvisor = snap.docs.filter(
          doc =>
            filterByDate(doc.data().createdAt) &&
            filterByOrg(doc.data().organization) &&
            filterByRole('moderator')
        ).length;
        setUserData(
          ROLE_LABELS.map(role => ({
            name: role.charAt(0).toUpperCase() + role.slice(1),
            value: counts[role],
            color: ROLE_COLORS[role],
          }))
        );
        setLoading(false);
      })
    );
    return () => unsubs.forEach(unsub => unsub());
  }, [selectedRole, selectedOrganization, dateRange]);

  const handleDateChange = (from, to) => setDateRange({ from, to });

  const hasData = userData.some(d => d.value > 0);

  return (
    <div className="mx-auto w-full max-w-2xl rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-nav md:p-6">
      <h3 className="mb-3 flex items-center gap-2 text-xl font-bold text-gray-900 dark:text-gray-100 md:mb-4 md:text-2xl">
        User Progression
      </h3>
      <div className="mb-4 flex flex-wrap items-center gap-2 md:mb-6 md:gap-3">
        <select
          value={selectedRole}
          onChange={e => setSelectedRole(e.target.value)}
          className="rounded border px-2 py-1 text-sm md:px-3 md:text-base"
        >
          {roles.map(r => (
            <option key={r} value={r}>
              {r.charAt(0).toUpperCase() + r.slice(1)}
            </option>
          ))}
        </select>
        <select
          value={selectedOrganization}
          onChange={e => setSelectedOrganization(e.target.value)}
          className="rounded border px-2 py-1 text-sm md:px-3 md:text-base"
        >
          {organizations.map(o => (
            <option key={o} value={o}>
              {o}
            </option>
          ))}
        </select>
        <input
          type="date"
          onChange={e => handleDateChange(new Date(e.target.value), dateRange.to)}
          className="rounded border px-2 py-1 text-sm md:px-3 md:text-base"
        />
        <span>-</span>
        <input
          type="date"
          onChange={e => handleDateChange(dateRange.from, new Date(e.target.value))}
          className="rounded border px-2 py-1 text-sm md:px-3 md:text-base"
        />
      </div>
      <div className="relative flex h-64 items-center justify-center rounded-xl border border-gray-100 bg-gradient-to-br from-blue-50 via-white to-emerald-50 dark:border-gray-800 md:h-80">
        <div className="pointer-events-none absolute inset-0">
          <svg width="100%" height="100%" className="h-full w-full">
            <defs>
              <pattern id="grid" width="32" height="32" patternUnits="userSpaceOnUse">
                <path d="M 32 0 L 0 0 0 32" fill="none" stroke="#e5e7eb" strokeWidth="1" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#grid)" />
          </svg>
        </div>
        {loading ? (
          <div className="h-12 w-12 animate-pulse rounded-full bg-accent/10 dark:bg-accent/90"></div>
        ) : hasData ? (
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={userData}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={80}
                paddingAngle={5}
                dataKey="value"
                label={({ name, percent }) =>
                  percent > 0 ? `${name} ${(percent * 100).toFixed(0)}%` : ''
                }
                labelLine={false}
              >
                {userData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        ) : (
          <div className="flex h-full w-full flex-col items-center justify-center">
            <UserGroupIcon className="mb-2 h-12 w-12 text-gray-300" />
            <p className="text-base text-gray-500 md:text-lg">No user data to display.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserProgressionChart;
